import { useEffect, useMemo, useState } from "react";
import { NavButtons } from "../components/NavButtons";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { getCached, setCached } from "@/lib/cache";
import PageLoader from "@/components/PageLoader";
import { toast } from "sonner";
import FilterPills from "@/components/FilterPills";
import { ArrowLeft, PaperPlaneTilt, CalendarBlank, WarningCircle, CheckCircle, ArrowsClockwise } from "@phosphor-icons/react";

const money = (n) => `$${(Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const parseDay = (s) => {
  if (!s) return null;
  const iso = String(s).slice(0, 10);
  const [y, m, day] = iso.split("-").map(Number);
  if (!y || !m || !day) return null;
  return new Date(y, m - 1, day);
};
const fmtDate = (d) => {
  if (!d) return "—";
  const parsed = parseDay(d) || new Date(d);
  if (isNaN(parsed.getTime())) return d;
  return parsed.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
};
const isoDay = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

function Stat({ label, value, sub, testid, tone = "default" }) {
  const toneStyle =
    tone === "danger" ? { color: "#c53030" } :
    tone === "success" ? { color: "var(--text)" } :
    {};
  return (
    <div className="panel p-5 md:p-6" data-testid={testid}>
      <p className="label">{label}</p>
      <p className="title-xl text-3xl md:text-4xl mt-2" style={toneStyle}>{value}</p>
      {sub && <p className="label mt-1 normal-case tracking-normal opacity-70">{sub}</p>}
    </div>
  );
}

function PaymentRow({ p, onRemind, remindDisabled }) {
  const nav = useNavigate();
  const go = (e) => {
    // Ignore clicks that originated on a button (Remind)
    if (e.target.closest("button")) return;
    nav(`/admin/clients/${p.client_user_id}?tab=payments`);
  };
  return (
    <div onClick={go}
      role="link" tabIndex={0}
      onKeyDown={(e) => { if (e.key === "Enter") nav(`/admin/clients/${p.client_user_id}?tab=payments`); }}
      className="hair p-3 flex flex-col md:flex-row md:items-center md:justify-between gap-2 cursor-pointer hover:bg-black/5 transition-colors"
      data-testid={`rev-row-${p.payment_id}`}>
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline gap-2 flex-wrap">
          <span className="font-medium truncate">{p.client_name || p.client_email}</span>
          <span className="label">{p.client_email}</span>
        </div>
        {p.note && <p className="text-xs mt-0.5 opacity-70 truncate">{p.note}</p>}
      </div>
      <div className="flex items-center gap-4 md:gap-6 shrink-0">
        <div className="text-right">
          <p className="font-semibold" data-testid={`rev-amount-${p.payment_id}`}>{money(p.amount)}</p>
          <p className="label mt-0.5">{fmtDate(p.due_date)}</p>
        </div>
        {onRemind && (
          <button
            data-testid={`rev-remind-${p.payment_id}`}
            onClick={(e) => { e.stopPropagation(); onRemind(p); }}
            disabled={remindDisabled}
            className="btn-ghost text-xs whitespace-nowrap disabled:opacity-40"
            title="Send payment reminder"
          >
            <PaperPlaneTilt size={12} weight="bold" /> Remind
          </button>
        )}
      </div>
    </div>
  );
}

function PaidRow({ p }) {
  const nav = useNavigate();
  return (
    <div onClick={() => nav(`/admin/clients/${p.client_user_id}?tab=payments`)}
      role="link" tabIndex={0}
      onKeyDown={(e) => { if (e.key === "Enter") nav(`/admin/clients/${p.client_user_id}?tab=payments`); }}
      className="hair p-3 flex flex-col md:flex-row md:items-center md:justify-between gap-2 cursor-pointer hover:bg-black/5 transition-colors"
      data-testid={`rev-paid-${p.payment_id}`}>
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline gap-2 flex-wrap">
          <span className="font-medium truncate">{p.client_name || p.client_email}</span>
          <span className="label">{p.client_email}</span>
        </div>
        {p.note && <p className="text-xs mt-0.5 opacity-70 truncate">{p.note}</p>}
      </div>
      <div className="text-right shrink-0">
        <p className="font-semibold">{money(p.amount)}</p>
        <p className="label mt-0.5">Paid {fmtDate(p.paid_at || p.due_date)}</p>
      </div>
    </div>
  );
}

export default function AdminRevenue() {
  const [data, setData] = useState(() => getCached("admin-revenue") ?? null);
  const [busyId, setBusyId] = useState(null);
  // Period filter: "next7" (default) | "next30" | "7d" | "30d" | "365d" | "custom" | "overdue"
  const [period, setPeriod] = useState("next7");
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get("/admin/revenue");
      setData(data);
      setCached("admin-revenue", data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load revenue"); }
  };
  useEffect(() => { load(); }, []);

  const remind = async (p) => {
    setBusyId(p.payment_id);
    try {
      await api.post(`/admin/clients/${p.client_user_id}/reminder-payment/${p.payment_id}`);
      toast.success("Reminder sent");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
    finally { setBusyId(null); }
  };

  const range = useMemo(() => {
    const today = new Date(); today.setHours(0, 0, 0, 0);
    if (period === "overdue") {
      return { start: null, end: null, label: "All overdue", forward: false, isOverdue: true };
    }
    if (period === "next7") {
      const end = new Date(today); end.setDate(end.getDate() + 7);
      return { start: today, end, label: "Next 7 days", forward: true };
    }
    if (period === "next30") {
      const end = new Date(today); end.setDate(end.getDate() + 30);
      return { start: today, end, label: "Next 30 days", forward: true };
    }
    if (period === "7d") {
      const start = new Date(today); start.setDate(start.getDate() - 6);
      return { start, end: today, label: "Last 7 days", forward: false };
    }
    if (period === "30d") {
      const start = new Date(today); start.setDate(start.getDate() - 29);
      return { start, end: today, label: "Last 30 days", forward: false };
    }
    if (period === "365d") {
      const start = new Date(today); start.setDate(start.getDate() - 364);
      return { start, end: today, label: "Last 365 days", forward: false };
    }
    // custom
    const start = parseDay(customStart) || (() => { const d = new Date(today); d.setDate(d.getDate() - 6); return d; })();
    const cEnd = parseDay(customEnd) || today;
    const label = `${fmtDate(isoDay(start))} → ${fmtDate(isoDay(cEnd))}`;
    return { start, end: cEnd, label, forward: cEnd >= today };
  }, [period, customStart, customEnd]);

  const filtered = useMemo(() => {
    if (!data) return null;
    if (range.isOverdue) {
      const overdue = data.overdue || [];
      const overdueTotal = overdue.reduce((s, p) => s + (Number(p.amount) || 0), 0);
      return { paid: [], upcoming: [], overdue, paidTotal: 0, upcomingTotal: 0, overdueTotal };
    }
    const inRange = (dstr) => {
      const d = parseDay(dstr);
      if (!d) return false;
      return d >= range.start && d <= range.end;
    };
    const paid = (data.recent_paid || []).filter(p => inRange(p.paid_at || p.due_date));
    const upcoming = (data.upcoming || []).filter(p => inRange(p.due_date));
    const overdue = (data.overdue || []).filter(p => inRange(p.due_date));
    const paidTotal = paid.reduce((s, p) => s + (Number(p.amount) || 0), 0);
    const upcomingTotal = upcoming.reduce((s, p) => s + (Number(p.amount) || 0), 0);
    const overdueTotal = overdue.reduce((s, p) => s + (Number(p.amount) || 0), 0);
    return { paid, upcoming, overdue, paidTotal, upcomingTotal, overdueTotal };
  }, [data, range]);

  if (!data || !filtered) return <PageLoader label="Loading revenue…" testid="admin-revenue-loader" />;

  // Headline reflects the current period
  const headlineValue = range.isOverdue
    ? filtered.overdueTotal
    : (range.forward ? filtered.upcomingTotal : filtered.paidTotal);
  const headlineCount = range.isOverdue
    ? filtered.overdue.length
    : (range.forward ? filtered.upcoming.length : filtered.paid.length);
  const headlineSuffix = range.isOverdue
    ? `${headlineCount} payment${headlineCount === 1 ? "" : "s"} overdue`
    : (range.forward
        ? `${headlineCount} payment${headlineCount === 1 ? "" : "s"} due in this window`
        : `${headlineCount} payment${headlineCount === 1 ? "" : "s"} received in this window`);

  return (
    <div className="min-h-screen overflow-x-hidden" data-testid="admin-revenue">
      <header className="flex items-center justify-between px-4 md:px-6 py-6 border-b gap-3" style={{ borderColor: "var(--hair)" }}>
        <div className="flex items-center gap-3 md:gap-4 min-w-0">
          <NavButtons home="/admin" testid="rev-back" />
          <div className="min-w-0">
            <p className="label">Console</p>
            <h1 className="title-xl text-2xl md:text-4xl mt-1 truncate">Revenue</h1>
          </div>
        </div>
        <button data-testid="rev-refresh" onClick={load} className="btn-ghost text-xs shrink-0" title="Refresh">
          <ArrowsClockwise size={14} weight="bold" /> <span className="hidden sm:inline">Refresh</span>
        </button>
      </header>

      <section className="p-4 md:p-6 space-y-8">
        {/* Period filter */}
        <div className="space-y-2" data-testid="rev-period-row">
          <FilterPills
            testid="rev-period"
            value={period}
            onChange={setPeriod}
            options={[
              { k: "next7", label: "Next 7 days" },
              { k: "next30", label: "Next 30 days" },
              { k: "7d", label: "Last 7 days" },
              { k: "30d", label: "Last 30 days" },
              { k: "365d", label: "Last 365 days" },
              { k: "custom", label: "Custom range" },
              { k: "overdue", label: "Overdue" },
            ]}
            trailing={<span className="label ml-auto" data-testid="rev-period-label">{range.label}</span>}
          />
          {period === "custom" && (
            <div className="flex flex-wrap items-center gap-2">
              <input type="date" value={customStart}
                data-testid="rev-custom-start"
                onChange={(e) => setCustomStart(e.target.value)}
                className="px-3 py-1.5 text-xs" />
              <span className="label">→</span>
              <input type="date" value={customEnd}
                data-testid="rev-custom-end"
                onChange={(e) => setCustomEnd(e.target.value)}
                className="px-3 py-1.5 text-xs" />
            </div>
          )}
        </div>

        {/* Headline metric */}
        <div>
          <p className="label">{range.label}</p>
          <div className="mt-2 flex items-baseline gap-4 flex-wrap">
            <p className="title-xl text-5xl md:text-6xl" data-testid="rev-window-total"
               style={range.isOverdue && headlineValue > 0 ? { color: "#c53030" } : {}}>
              {money(headlineValue)}
            </p>
            <p className="label">{headlineSuffix}</p>
          </div>
        </div>

        {/* Single dynamic panel */}
        {range.isOverdue ? (
          <div className="panel p-4 md:p-6" data-testid="rev-overdue-panel">
            <div className="flex items-center gap-2 mb-3">
              <WarningCircle size={16} weight="bold" />
              <h2 className="title-xl text-xl">Overdue</h2>
              <span className="label ml-auto">All overdue</span>
            </div>
            {filtered.overdue.length === 0 ? (
              <p className="label py-8 text-center">Nothing overdue</p>
            ) : (
              <div className="space-y-2">
                {filtered.overdue.map(p => (
                  <PaymentRow key={p.payment_id} p={p} onRemind={remind} remindDisabled={busyId === p.payment_id} />
                ))}
              </div>
            )}
          </div>
        ) : range.forward ? (
          <div className="panel p-4 md:p-6" data-testid="rev-upcoming-panel">
            <div className="flex items-center gap-2 mb-3">
              <CalendarBlank size={16} weight="bold" />
              <h2 className="title-xl text-xl">Upcoming payments</h2>
              <span className="label ml-auto">{range.label}</span>
            </div>
            {filtered.upcoming.length === 0 ? (
              <p className="label py-8 text-center">No upcoming payments in this window</p>
            ) : (
              <div className="space-y-2">
                {filtered.upcoming.map(p => (
                  <PaymentRow key={p.payment_id} p={p} onRemind={remind} remindDisabled={busyId === p.payment_id} />
                ))}
              </div>
            )}
          </div>
        ) : (
          <div className="panel p-4 md:p-6" data-testid="rev-paid-panel">
            <div className="flex items-center gap-2 mb-3">
              <CheckCircle size={16} weight="bold" />
              <h2 className="title-xl text-xl">Payments received</h2>
              <span className="label ml-auto">{range.label}</span>
            </div>
            {filtered.paid.length === 0 ? (
              <p className="label py-8 text-center">No payments received in this window</p>
            ) : (
              <div className="space-y-2">
                {filtered.paid.map(p => (
                  <PaidRow key={p.payment_id} p={p} />
                ))}
              </div>
            )}
          </div>
        )}
      </section>
    </div>
  );
}
